import { defineComponent as _defineComponent } from 'vue'
import BFieldWrapper from './BFieldWrapper.vue';
import Icon from 'Src/js/components/Icon.vue';
import { computed, nextTick, ref, watch } from 'vue';
import { v4 as genUUID } from 'uuid';
import { useDefaultInputLabelVariant } from "./input-label-variant";
import { useFormRegistration } from './form-registration';
import { useViewportWidth } from 'JS/composables/breakpoint';


export default /*#__PURE__*/_defineComponent({
  __name: 'BSelect',
  props: {
    id: null,
    label: null,
    value: { default: '' },
    required: { type: Boolean },
    hideRequirementFlag: { type: Boolean },
    disabled: { type: Boolean },
    error: { default: '' },
    noAutoValidation: { type: Boolean },
    placeholder: null,
    labelDisplayType: null
  },
  emits: ["input"],
  setup(__props: any, { emit }: { emit: ({
	(event: 'input', value: string | number): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { id?: string, label: string, value: string | number, required?: boolean, hideRequirementFlag?: boolean, disabled?: boolean, error: string, noAutoValidation?: boolean, placeholder?: string, labelDisplayType?: "normal" | "inside" | "inline" }





const injectedLabelVariant = useDefaultInputLabelVariant();

/**
 * Internally managed field value state
 */
const internalValue = ref(props.value);

/**
 * Internal error message field for validation errors
 */
const internalErrorMessage = ref('');

/**
 * Whether or not field should validate on input
 */
const shouldValidate = ref(false);

/**
 * Proxies input event to `setInternalValue`
 */
function handleInput(e: InputEvent & { target: HTMLInputElement | HTMLTextAreaElement }) {
	setInternalValue(e.target.value);
}

/**
 * Handles state, events, and validations for internal field value when changed internally or externally
 */
function setInternalValue(value: string | number) {
	internalValue.value = value;
	emit('input', value);

	if (shouldValidate.value) {
		const error = validate();
		if (error === true) {
			shouldValidate.value = false;
		}
	}
}

const uuid = genUUID();
/**
 * Id used for checkbox and ARIA relationships. `id` prop if passed,
 * otherwise generated unique id
 */
const selectElementId = computed(() => {
	return props.id || uuid;
});

/**
 * Returns the `error` props if it's not undefined or empty and the `internalErrorMessage` otherwise
 */
const errorMessage = computed(() => {
	return props.error || internalErrorMessage.value;
});

const styleAsPlaceholder = computed(() => {
	return !!props.placeholder && !props.value;
});

const internalLabelVariant = computed((): 'normal' | 'inside' | 'inline'  => {
	if (props.labelDisplayType) return props.labelDisplayType;
	if ( injectedLabelVariant?.value === "inline") return "inline";
	return 'normal';
});

/**
 * Handles validation behavior when field is blurred
 */
function onBlur() {
	if (props.noAutoValidation) {
		return;
	}

	startValidating();
}

/**
 * Enables on-change field validation and runs the initial validation
 */
function startValidating() {
	shouldValidate.value = true;
	const valid = validate();
	if (valid === true) {
		shouldValidate.value = false;
	}
}

/**
 * IValidatable implementation.
 */
function validate(): string | true {
	if (props.required && !props.value) {
		internalErrorMessage.value = 'Please make a selection.';
		shouldValidate.value = true;
		return internalErrorMessage.value;
	}

	internalErrorMessage.value = '';
	return true;
}

/**
 * IValidatable implementation.
 */
function resetValidation() {
	shouldValidate.value = false;
	internalErrorMessage.value = '';
}

useFormRegistration({
	validate,
	resetValidation,
});

watch(() => props.value, value => {
	setInternalValue(value);
});

watch(() => props.error, error => {
	internalErrorMessage.value = error;
});

const { selectLeftPadding, insideLabel } = useInsideLabelSelectPadding();
/**
 * Handles size calculations for the "inside" labelDisplayType variant. Unfortunately, we couldn't
 * find a reasonable way to do this with CSS only.
 * Alternatives tried:
 *   - Placing a pseudo element of the label over top of the container. Unlike checkboxes/radios,
 *     clicking on a select's label element does not trigger it to open
 *   - Placing a pseudo element of the select over top of the container. Apparently ::before and ::after
 *     don't work on select elements...
 *   - Make the select itself invisible and place it over top of the container and render the option label
 *     of the current value independently. This required reaching into the DOM to grab the textContent of the
 *     currently-selected option. This was the most reasonable of the other options, but I was not comfortable
 *     enumerating all of the edge cases of relying on the DOM in that way. I'd rather allow the select element
 *     to render the content on its own
 *
 * NOTE: I put this into a function to more clearly group this functionality. It is not general enough to
 *       be pulled out into its own module for reuse.
 */
function useInsideLabelSelectPadding() {
	const viewportWidth = useViewportWidth();

	// state/DOM handles for the inside label variant calculations below
	const selectLeftPadding = ref<string | null>(null);
	const insideLabel = ref<HTMLElement | null>(null);

	watch(() => [
		props.labelDisplayType,
		// watching the DOM ref so that this runs after mount, ie. when it's actually in the DOM
		insideLabel.value,
		// not used directly in watcher, but need to recalculate if label changes
		props.label,
		// not used directly in watcher, but need to recalculate when screen size changes. handles cases where
		// external (or future internal) styles modify the label styles at different breakpoints
		viewportWidth.value,
	] as const, ([currentLabelDisplayType, insideLabelEl]) => {
		nextTick(() => {
			if (!insideLabelEl || currentLabelDisplayType !== 'inside') {
				selectLeftPadding.value = null;
			} else {
				const { width } = insideLabelEl.getBoundingClientRect();
				selectLeftPadding.value = `${width + 4}px`;
			}
		});
	}, {
		immediate: true,
	});

	return { selectLeftPadding, insideLabel };
}


return { __sfc: true,props, emit, injectedLabelVariant, internalValue, internalErrorMessage, shouldValidate, handleInput, setInternalValue, uuid, selectElementId, errorMessage, styleAsPlaceholder, internalLabelVariant, onBlur, startValidating, validate, resetValidation, selectLeftPadding, insideLabel, useInsideLabelSelectPadding, BFieldWrapper, Icon }
}

})